import { Dialog } from '@headlessui/react';
import React, { useCallback, useEffect } from 'react';
import { useSpring, a, animated, easings } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import { DragHandleIcon, useIsEmployerApp, cn } from '@restworld/utils-common';
import { Button } from '../../button';

export interface MobileModalWithGesturesProps {
  open: boolean;
  title?: string | React.ReactNode;
  onClose: () => void;
  dragHandleIcon?: React.ReactNode;
  children?: React.ReactNode;
  showMobileGestures?: boolean;
  noPadding?: boolean;
  showDragHandleIcon?: boolean;
  modalStyles?: string[];
  footer?: React.ReactNode;
  footerStyles?: string[];
  primaryActionLabel?: string;
  onClickPrimaryAction?: () => void;
  secondaryActionLabel?: string;
  onClickSecondaryAction?: () => void;
  padBodyBottom?: boolean;
  hasHeaderBorderBottom?: boolean;
  headerStyles?: string[];
  disableDrag?: boolean; // New prop to disable dragging
  hasOffWhiteBackground?: boolean;
}

export function MobileModalWithGestures({
  dragHandleIcon,
  title,
  children,
  open,
  onClose,
  noPadding,
  showDragHandleIcon = true,
  modalStyles,
  footer,
  footerStyles,
  primaryActionLabel,
  onClickPrimaryAction,
  secondaryActionLabel,
  onClickSecondaryAction,
  padBodyBottom,
  hasHeaderBorderBottom,
  headerStyles,
  disableDrag = false, // Default value for the new prop
  hasOffWhiteBackground,
}: MobileModalWithGesturesProps) {
  const height = 646;
  const [{ y }, api] = useSpring(() => ({ y: height }));
  const { isEmployerApp } = useIsEmployerApp();

  const openModal = useCallback(
    ({ canceled }: { canceled: boolean }) => {
      api.start({
        y: 0,
        immediate: true,
        config: { easing: canceled ? easings.easeInSine : easings.linear },
      });
    },
    [api]
  );

  const closeModal = useCallback(
    (_velocity = 0, y = height) => {
      api.start({ y, immediate: false, config: { easing: easings.easeOutElastic } });
      onClose();
    },
    [api, onClose]
  );

  useEffect(() => {
    if (open) {
      openModal({ canceled: false });
    }
  }, [open, openModal]);

  const bindGesture = useDrag(
    ({ last, velocity: [, vy], direction: [, dy], offset: [, oy], cancel, canceled }) => {
      // Determine the threshold for closing the modal (you can adjust this)
      const closeThreshold = height * 0.3;

      // Apply opacity to the modal
      api.start({ y: oy, immediate: true });

      // if the user drags up passed a threshold, then we cancel
      // the drag so that the sheet resets to its open position
      if (oy < -closeThreshold) cancel();

      // when the user releases the sheet, we check whether it passed
      // the threshold for it to close, or if we reset it to its open position
      if (last) {
        if (oy > closeThreshold || (vy > 0.5 && dy > 0)) {
          closeModal(vy);
        } else {
          openModal({ canceled });
        }
      }
    },
    {
      from: () => [0, y.get()],
      filterTaps: true,
      bounds: { top: 0 },
      rubberband: true,
    }
  );

  const display = y.to((py) => (py <= height ? 'flex' : 'none'));
  return (
    <StyledMobileDialogPanel
      style={{ y, display }}
      hasOffWhiteBackground={hasOffWhiteBackground || isEmployerApp}
      noPadding={noPadding}
    >
      <MobileHeaderStyled headerStyles={headerStyles} {...(disableDrag ? {} : bindGesture())} noPadding={noPadding}>
        {showDragHandleIcon ? (
          <div className="flex flex-row w-full justify-center box-border  py-6 px-4">
            <button className="border-none outline-none">{dragHandleIcon || <DragHandleIcon />}</button>
          </div>
        ) : null}
        {title && (
          <div
            className={`flex flex-row w-full justify-center ${
              hasHeaderBorderBottom ? 'border-b border-neutral-40 pb-2.5' : ''
            }`}
          >
            {typeof title === 'string' ? (
              <h3 className="text-gray-900 font-semibold text-xl leading-6 text-center">{title}</h3>
            ) : (
              title
            )}
          </div>
        )}
      </MobileHeaderStyled>
      <MobileModalBody noPadding={noPadding} modalStyles={modalStyles} padBottom={padBodyBottom}>
        {children}
      </MobileModalBody>

      {(footer || primaryActionLabel || secondaryActionLabel) && (
        <MobileModalFooter noPadding={noPadding} footerStyles={footerStyles}>
          {footer}
          {(primaryActionLabel || secondaryActionLabel) && (
            <div className="flex flex-col items-start w-full gap-4">
              {primaryActionLabel && (
                <Button
                  fullwidth
                  title={primaryActionLabel}
                  variant="normal"
                  onClick={() => {
                    if (onClickPrimaryAction) {
                      onClickPrimaryAction();
                    }
                  }}
                />
              )}
              {secondaryActionLabel && (
                <Button
                  fullwidth
                  title={secondaryActionLabel}
                  variant="secondary-outline"
                  onClick={() => {
                    if (onClickSecondaryAction) {
                      onClickSecondaryAction();
                    }
                  }}
                />
              )}
            </div>
          )}
        </MobileModalFooter>
      )}
    </StyledMobileDialogPanel>
  );
}
const AnimatedDialogPanel = animated(Dialog.Panel);
const StyledMobileDialogPanel = ({ hasOffWhiteBackground, noPadding, className, ...props }: any) => (
  <AnimatedDialogPanel
    className={cn(
      'flex flex-col items-center justify-start box-border bg-neutral-10 min-h-[75dvh]',
      'min-w-full h-fit max-w-[100vw] max-h-[90dvh] rounded-tl-6.25 rounded-tr-6.25 touch-none',
      hasOffWhiteBackground && 'bg-neutral-10',
      noPadding && 'pb-0',
      className
    )}
    {...props}
  />
);
const MobileHeaderStyled = ({ headerStyles, noPadding, className, ...props }: any) => (
  <a.div
    className={cn(
      'flex w-full flex-col items-start justify-start gap-2 touch-none pb-4',
      ...(headerStyles || []),
      noPadding && 'pb-0',
      className
    )}
    {...props}
  />
);
const MobileModalBody = ({ noPadding, modalStyles, padBottom, className, ...props }: any) => (
  <div
    className={cn(
      'flex flex-col flex-1 grow items-start box-border w-full max-w-[100vw] overflow-y-auto',
      'px-6',
      noPadding && 'px-0',
      padBottom && 'mb-16',
      className
    )}
    {...props}
  />
);

const MobileModalFooter = ({ noPadding, footerStyles, className, ...props }: any) => (
  <div
    className={cn(
      'flex flex-col items-start box-border w-full py-4 px-6 self-end border-t border-neutral-40',
      noPadding ? '!px-0 !py-0' : '',
      className
    )}
    {...props}
  />
);
